import { useMemo, useState } from 'react';
import { useAppData } from '../store';
import ObligationCard from './ObligationCard';
import type { Obligation } from '../types';
import { CATEGORY_LABELS, type PropertyCategory } from '../types';

type FilterKey = 'all' | 'overdue' | 'due_soon' | 'recurring' | 'one_time' | 'inactive';

export default function SearchView() {
  const { obligations, properties, remindersByObligation } = useAppData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [categoryFilter, setCategoryFilter] = useState<PropertyCategory | 'all'>('all');
  const [propertyFilter, setPropertyFilter] = useState<string | 'all'>('all');

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return obligations.filter((o: Obligation) => {
      if (filter === 'inactive' && o.active) return false;
      if (filter !== 'inactive' && !o.active) return false;

      const property = properties.find((p) => p.id === o.property_id);

      if (categoryFilter !== 'all' && property?.category !== categoryFilter) return false;
      if (propertyFilter !== 'all' && o.property_id !== propertyFilter) return false;

      if (filter === 'recurring' && o.recurrence_type === 'none') return false;
      if (filter === 'one_time' && o.recurrence_type !== 'none') return false;

      if (filter === 'overdue' && new Date(o.due_date) >= new Date(new Date().toDateString()))
        return false;
      if (filter === 'due_soon') {
        const diffDays =
          (new Date(o.due_date).getTime() - new Date(new Date().toDateString()).getTime()) /
          86400000;
        if (diffDays < 0 || diffDays > 30) return false;
      }

      if (!q) return true;
      const haystack = [o.name, o.description, o.notes, o.payee, property?.name, property?.category]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [obligations, properties, query, filter, categoryFilter, propertyFilter]);

  return (
    <div className="search-view">
      <h1>Search</h1>
      <input
        className="search-input"
        type="search"
        placeholder="insurance, contractor, registration…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />

      <div className="filter-row">
        <select value={filter} onChange={(e) => setFilter(e.target.value as FilterKey)}>
          <option value="all">All active</option>
          <option value="overdue">Overdue</option>
          <option value="due_soon">Due soon</option>
          <option value="recurring">Recurring</option>
          <option value="one_time">One-time</option>
          <option value="inactive">Completed / inactive</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as PropertyCategory | 'all')}
        >
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <select value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)}>
          <option value="all">All properties</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <p className="muted">
        {results.length} result{results.length === 1 ? '' : 's'}
      </p>

      <div className="card-list">
        {results.map((o) => (
          <ObligationCard key={o.id} obligation={o} reminders={remindersByObligation[o.id]} />
        ))}
      </div>
    </div>
  );
}
